
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, Search, User, Calendar, Clock, FileText, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import ConsultationNotesModal from "@/components/psychologist/ConsultationNotesModal";
import { handleError } from "@/utils/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface MedicalRecordsTabProps {
  psychologistId?: string;
}

interface MedicalRecord {
  id: string;
  appointment_id: string;
  patient_id: string;
  notes: string;
  created_at: string;
  updated_at: string;
  appointments: {
    date: string;
    time: string;
    duration: number;
    status: string;
    user_id: string;
  };
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

const MedicalRecordsTab = ({ psychologistId }: MedicalRecordsTabProps) => {
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  useEffect(() => {
    if (psychologistId) {
      loadMedicalRecords();
    }
  }, [psychologistId]);

  useEffect(() => {
    // Filtrar registros por nome do paciente
    if (searchTerm.trim() === "") {
      setFilteredRecords(records);
    } else {
      const filtered = records.filter(record =>
        `${record.profiles.first_name} ${record.profiles.last_name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
      setFilteredRecords(filtered);
    }
  }, [searchTerm, records]);

  const loadMedicalRecords = async () => {
    if (!psychologistId) return;

    setLoading(true);
    setError(null);
    
    try {
      // Buscar notas de consulta
      const { data: notes, error: notesError } = await supabase
        .from('consultation_notes')
        .select(`
          id,
          appointment_id,
          patient_id,
          notes,
          created_at,
          updated_at
        `)
        .eq('psychologist_id', psychologistId)
        .order('updated_at', { ascending: false });

      if (notesError) throw notesError;

      if (!notes || notes.length === 0) {
        setRecords([]);
        return;
      }

      // Buscar informações dos agendamentos
      const appointmentIds = notes.map(note => note.appointment_id);
      const { data: appointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select('id, date, time, duration, status, user_id')
        .in('id', appointmentIds);

      if (appointmentsError) throw appointmentsError;

      // Buscar perfis dos usuários
      const userIds = [...new Set(appointments?.map(apt => apt.user_id) || [])];
      
      if (userIds.length === 0) {
        setRecords([]);
        return;
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      if (profilesError) {
        console.error('Erro ao carregar perfis:', profilesError);
      }

      // Criar mapas para facilitar a combinação
      const appointmentsMap = new Map(appointments?.map(apt => [apt.id, apt]) || []);
      const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Combinar dados
      const enrichedRecords = notes.map(note => {
        const appointment = appointmentsMap.get(note.appointment_id);
        const profile = profilesMap.get(appointment?.user_id);
        
        return {
          ...note,
          appointments: appointment || {
            date: '',
            time: '',
            duration: 0,
            status: '',
            user_id: ''
          },
          profiles: profile || {
            first_name: 'Usuário',
            last_name: '',
            email: ''
          }
        };
      });

      setRecords(enrichedRecords);
    } catch (error) {
      console.error('Error loading medical records:', error);
      setError('Erro ao carregar prontuários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      confirmed: { label: 'Confirmada', variant: 'default' as const },
      pending: { label: 'Pendente', variant: 'secondary' as const },
      cancelled: { label: 'Cancelada', variant: 'destructive' as const },
      completed: { label: 'Concluída', variant: 'outline' as const }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleViewNotes = (record: MedicalRecord) => {
    // Criar objeto compatível com o modal existente
    const appointmentForModal = {
      id: record.appointment_id,
      date: record.appointments.date,
      time: record.appointments.time,
      duration: record.appointments.duration,
      status: record.appointments.status,
      user_id: record.patient_id,
      profiles: {
        first_name: record.profiles.first_name,
        last_name: record.profiles.last_name,
        email: record.profiles.email
      }
    };

    setSelectedRecord(appointmentForModal);
    setNotesModalOpen(true);
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Carregando prontuários..." />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-red-600">{error}</p>
          <Button 
            onClick={loadMedicalRecords} 
            variant="outline" 
            className="mt-2"
          >
            Tentar Novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Prontuários dos Pacientes
          </CardTitle>
          <CardDescription>
            Consulte e gerencie todos os prontuários dos seus pacientes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por nome do paciente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Records List */}
          {filteredRecords.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nenhum prontuário encontrado para a busca' : 'Nenhum prontuário encontrado'}
              </p>
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Tente outro termo de busca' : 'Prontuários aparecerão aqui conforme você atende pacientes'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRecords.map(record => (
                <Card key={record.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {record.profiles.first_name} {record.profiles.last_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(record.appointments.status)}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewNotes(record)}
                          title="Visualizar prontuário"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{record.appointments.date ? format(new Date(record.appointments.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : 'Data não disponível'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{record.appointments.time} ({record.appointments.duration} min)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span>Última atualização: {format(new Date(record.updated_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                      </div>
                    </div>

                    {/* Preview das notas */}
                    <div className="mt-3 p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Prévia das anotações:</p>
                      <p className="text-sm line-clamp-2">
                        {record.notes.length > 150 
                          ? `${record.notes.substring(0, 150)}...` 
                          : record.notes
                        }
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consultation Notes Modal */}
      {selectedRecord && psychologistId && (
        <ConsultationNotesModal
          isOpen={notesModalOpen}
          onClose={() => {
            setNotesModalOpen(false);
            setSelectedRecord(null);
            // Recarregar dados após fechar o modal
            loadMedicalRecords();
          }}
          appointment={selectedRecord}
          psychologistId={psychologistId}
        />
      )}
    </div>
  );
};

export default MedicalRecordsTab;
